import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, X, Bookmark, CalendarCheck, Heart } from "lucide-react";

const BANNER_KEY = "dh_signup_banner_dismissed";
const MODAL_KEY = "dh_signup_modal_seen";
const VIEW_THRESHOLD = 3;

export default function SignupPromos() {
  const { user, ready } = useAuth();
  const { pick } = useLang();
  const location = useLocation();
  const [bannerDismissed, setBannerDismissed] = useState(() => !!localStorage.getItem(BANNER_KEY));
  const [modalOpen, setModalOpen] = useState(false);

  // Open the signup modal once the visitor has seen a few DISTINCT pages (logged-out only).
  // Distinct paths are persisted to sessionStorage so React StrictMode re-mounts can't over/under count,
  // and we avoid timers whose cleanup could cancel the open on the next navigation.
  useEffect(() => {
    if (!ready || user) return;
    if (localStorage.getItem(MODAL_KEY)) return;
    let paths = [];
    try { paths = JSON.parse(sessionStorage.getItem("dh_paths") || "[]"); } catch { paths = []; }
    if (!paths.includes(location.pathname)) {
      paths.push(location.pathname);
      sessionStorage.setItem("dh_paths", JSON.stringify(paths));
    }
    // Auto-open disabled — the dismissible top banner prompts signups
    // without blocking clicks on page CTAs. (paths still tracked for future use.)
    void VIEW_THRESHOLD;
  }, [location.pathname, ready, user]);

  const closeModal = () => {
    localStorage.setItem(MODAL_KEY, "1");
    setModalOpen(false);
  };
  const dismissBanner = () => {
    localStorage.setItem(BANNER_KEY, "1");
    setBannerDismissed(true);
  };

  if (!ready || user) return null;

  const benefits = [
    { Icon: Bookmark, text: pick("मंत्र, कथा एवं स्तोत्र सहेजें", "Save mantras, kathas & stotras") },
    { Icon: CalendarCheck, text: pick("पूजा एवं सेवाओं की बुकिंग ट्रैक करें", "Track your pooja & service bookings") },
    { Icon: Heart, text: pick("अपनी पसंद की सामग्री एक स्थान पर", "Keep your favourites in one place") },
  ];

  return (
    <>
      {!bannerDismissed && (
        <div data-testid="signup-banner" className="relative z-30 border-b border-line bg-[var(--surface-alt)]/70">
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-5 py-2.5 text-center md:px-8">
            <Sparkles className="hidden h-4 w-4 shrink-0 text-saffron sm:block" />
            <p className="text-sm text-ink-2">
              {pick("निःशुल्क खाता बनाएँ और अपनी पसंद की सामग्री सहेजें एवं ट्रैक करें।",
                    "Create a free account to save & track your favourite knowledge.")}
            </p>
            <Link to="/register" data-testid="signup-banner-cta" className="shrink-0 whitespace-nowrap border border-saffron px-3 py-1 text-xs font-medium text-saffron transition-colors hover:bg-saffron hover:text-white">
              {pick("खाता बनाएँ", "Sign up")}
            </Link>
            <button onClick={dismissBanner} data-testid="signup-banner-dismiss" aria-label="Dismiss" className="absolute right-4 text-ink-3 transition-colors hover:text-ink">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <DialogContent data-testid="signup-modal" className="max-w-md rounded-none border-line bg-surface p-0">
          <div className="border-b border-line bg-[var(--parchment)] px-7 py-8 text-center">
            <p className="font-deva text-3xl text-ink">धर्मार्थ</p>
            <p className="mt-2 overline-label text-gold">{pick("निःशुल्क सदस्यता", "Free membership")}</p>
          </div>
          <div className="px-7 py-7">
            <DialogTitle className="font-serif-latin text-2xl font-normal leading-snug text-ink">
              {pick("अपनी आध्यात्मिक यात्रा सहेजें", "Save your spiritual journey")}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-ink-2">
              {pick("एक निःशुल्क धर्मार्थ खाता बनाएँ और इन सुविधाओं का लाभ उठाएँ:",
                    "Create a free Dharmartha account and unlock:")}
            </DialogDescription>
            <ul className="mt-5 space-y-3">
              {benefits.map(({ Icon, text }, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-ink-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-line text-saffron"><Icon className="h-4 w-4" /></span>
                  {text}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col gap-2.5">
              <Link to="/register" onClick={closeModal} data-testid="signup-modal-cta" className="inline-flex items-center justify-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                {pick("निःशुल्क खाता बनाएँ", "Create free account")}
              </Link>
              <button onClick={closeModal} data-testid="signup-modal-later" className="py-2 text-sm text-ink-3 transition-colors hover:text-ink">
                {pick("अभी नहीं", "Maybe later")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
